import React, { useMemo } from 'react';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { Box, Alert, Paper, useTheme } from '@mui/material';

import { useScrapersData } from '../../../hooks/useKeywordsData';
import { useInlineEdit } from '../../../hooks/useInlineEdit';
import { createScraperOverviewColumns } from '../../../utils/scraperOverviewColumns';
import { getTableStyles, getRowStyles, initialTableState } from '../../../utils/mabScrapingTableConfig';

export default function ScraperOverview() {
  const theme = useTheme();
  const { rows, loading, error, setError, clearError, updateScraper } = useScrapersData();

  const {
    editingRowId,
    editedValues,
    saving,
    startEdit,
    cancelEdit,
    saveEdit,
    updateField,
  } = useInlineEdit({
    editableFields: ['active', 'interval_minutes'],
    onSave: updateScraper,
    onError: setError,
  });

  const columns = useMemo(
    () =>
      createScraperOverviewColumns({
        editingRowId,
        editedValues,
        saving,
        onStartEdit: startEdit,
        onCancelEdit: cancelEdit,
        onSaveEdit: saveEdit,
        onUpdateField: updateField,
      }),
    [editingRowId, editedValues, saving, startEdit, cancelEdit, saveEdit, updateField],
  );

  const tableStyles = useMemo(() => getTableStyles(theme), [theme]);

  const table = useMaterialReactTable({
    columns,
    data: rows,
    getRowId: (row) => row._id,
    initialState: {
      ...initialTableState,
      columnVisibility: { _id: false },
    },
    state: { isLoading: loading },
    enableEditing: true,
    editDisplayMode: 'custom',
    enableStickyHeader: false,
    enableColumnFilters: true,
    enableGlobalFilter: true,
    enableSorting: true,
    enablePagination: true,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableColumnActions: false,
    enableColumnDragging: false,
    ...tableStyles,
    muiTableBodyRowProps: ({ row }) =>
      getRowStyles(theme, editingRowId === row.original._id),
  });

  if (error && !rows.length) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Error loading scrapers: {error}
      </Alert>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={clearError} sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}
      <Paper
        elevation={0}
        sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}
      >
        <MaterialReactTable table={table} />
      </Paper>
    </Box>
  );
}
